import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUsers } from '@/api/base';
import { api } from '@/api/client';
import { useLead, useM3Mutation } from '@/api/m3';
import type { LeadCard } from '@/api/m3-types';
import { useMe } from '@/api/queries';
import { ActionMenu } from '@/components/kit/ActionMenu';
import { StageDot } from '@/components/kit/Board';
import { Button } from '@/components/kit/Button';
import { Breadcrumbs, Kv } from '@/components/kit/Layout';
import { Alert, ErrorState, Note, Skeleton } from '@/components/kit/Note';
import { OwnerPill } from '@/components/kit/OwnerPill';
import { Overview, Panel, PanelEmpty, Signals } from '@/components/kit/Panel';
import { Avatar, PersonHeader } from '@/components/kit/Person';
import { Pill } from '@/components/kit/Pill';
import { useToast } from '@/components/kit/Toast';
import { money } from '@/lib/format';
import { formatPhone } from '@/lib/phone';
import { errorText } from '@/features/base/shared/useUndoable';
import { dateTime, deals, fullDateTime, LEAD_STATUS, minutesText, stageTitle, stageTone } from '../labels';
import { M3Feed, TasksStub } from '../M3Feed';
import { useM3Action } from '../useM3Submit';
import { DuplicateModal, QualifyModal, RejectModal } from './LeadDialogs';

type Dialog = 'qualify' | 'reject' | 'dup';

/**
 * Карточка заявки (viewLead в макете, ТЗ 5.1): ответственный — пилюлей
 * в шапке с вариантом «Ничья», главное действие по состоянию, справа
 * задачи и лента.
 */
export function LeadPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const card = useLead(leadId);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const close = () => setDialog(null);
  const crumbs = (last: string) => (
    <Breadcrumbs
      items={[
        { label: 'CRM', onClick: () => navigate('/crm/leads') },
        { label: 'Заявки', onClick: () => navigate('/crm/leads') },
        { label: last },
      ]}
    />
  );

  if (card.isError) {
    return (
      <div className="main">
        {crumbs('Заявка')}
        <ErrorState message={card.error.message} onRetry={() => void card.refetch()} />
      </div>
    );
  }
  if (card.isLoading || !card.data) {
    return (
      <div className="main">
        {crumbs('…')}
        <Skeleton width={320} height={28} />
        <div className="sigs" />
        <Skeleton width="60%" />
      </div>
    );
  }

  const l = card.data;
  return (
    <div className="main has-abar">
      {crumbs(`#${l.id}`)}
      <LeadHeader lead={l} onDialog={setDialog} />
      <LeadSignals lead={l} />
      <Overview
        left={
          <>
            <LeadPanel lead={l} />
            <ClientPanel lead={l} />
            <SourcePanel lead={l} />
            {l.deals.length ? (
              <Panel title="Сделки из заявки" count={l.deals.length}>
                {l.deals.map((d) => (
                  <div className="lrow" key={d.id} role="link" onClick={() => navigate(`/crm/deals/${d.id}`)}>
                    <StageDot tone={stageTone(d.stage)} />
                    <div className="x">
                      <div className="n">{d.label}</div>
                      <div className="s">
                        {stageTitle(d.stage)} · {d.clientName}
                      </div>
                    </div>
                    <div className="v num">{d.plan ? money(d.plan) : ''}</div>
                    <span className="chev">›</span>
                  </div>
                ))}
              </Panel>
            ) : null}
            {l.sameNumber.length ? (
              <Panel title="Заявки с этого номера" count={l.sameNumber.length}>
                {l.sameNumber.map((m) => (
                  <div className="lrow" key={m.id} role="link" onClick={() => navigate(`/crm/leads/${m.id}`)}>
                    <Pill tone={LEAD_STATUS[m.status].tone}>{LEAD_STATUS[m.status].label}</Pill>
                    <div className="x">
                      <div className="n">
                        #{m.id} · {m.source}
                      </div>
                      <div className="s">
                        {fullDateTime(m.createdAt)}
                        {m.event ? ` · ${m.event.city}` : ''}
                      </div>
                    </div>
                    <div className="v" />
                    <span className="chev">›</span>
                  </div>
                ))}
              </Panel>
            ) : null}
          </>
        }
        right={
          <>
            <TasksStub of="заявке" />
            <M3Feed scope="leads" id={String(l.id)} />
          </>
        }
      />
      {dialog === 'qualify' ? <QualifyModal lead={l} onClose={close} /> : null}
      {dialog === 'reject' ? <RejectModal lead={l} onClose={close} /> : null}
      {dialog === 'dup' ? <DuplicateModal lead={l} onClose={close} /> : null}
    </div>
  );
}

function LeadHeader({ lead: l, onDialog }: { lead: LeadCard; onDialog: (d: Dialog) => void }) {
  const users = useUsers();
  const me = useMe();
  const toast = useToast();
  const action = useM3Action();
  const setOwner = useM3Mutation((userId: string | null) => api.put(`/leads/${l.id}/owner`, { userId }));
  const status = LEAD_STATUS[l.status];

  // Главное действие — одно; второстепенные: три — «⋯», два — тихими кнопками (кит)
  let main = null;
  let extra: { label: string; onClick: () => void }[] = [];
  if (l.status === 'new') {
    main = (
      <Button tone="primary" disabled={action.pending} onClick={() => action.run(() => api.post(`/leads/${l.id}/take`), 'Заявка в работе, касание зафиксировано')}>
        Взять в работу
      </Button>
    );
    extra = [
      { label: 'Квалифицировать без взятия в работу', onClick: () => onDialog('qualify') },
      { label: 'Отказ…', onClick: () => onDialog('reject') },
      { label: 'Отметить дублем…', onClick: () => onDialog('dup') },
    ];
  }
  if (l.status === 'in_progress') {
    main = (
      <Button tone="primary" onClick={() => onDialog('qualify')}>
        Квалифицировать
      </Button>
    );
    extra = [
      { label: 'Отказ…', onClick: () => onDialog('reject') },
      { label: 'Отметить дублем…', onClick: () => onDialog('dup') },
    ];
  }
  if (l.status === 'rejected' || l.status === 'duplicate') {
    main = (
      <Button tone="primary" disabled={action.pending} onClick={() => action.run(() => api.post(`/leads/${l.id}/reopen`), 'Заявка снова в работе')}>
        Вернуть в работу
      </Button>
    );
  }

  return (
    <PersonHeader
      initials={l.initials}
      title={l.title}
      pills={<Pill tone={status.tone}>{status.label}</Pill>}
      meta={<span>Заявка #{l.id}</span>}
      actions={
        <>
          <OwnerPill
            owner={l.owner}
            users={users.data ?? []}
            meId={me.data?.id}
            noneOption="Ничья"
            onChange={(id) =>
              setOwner.mutate(id, {
                onSuccess: () => toast.show(`Ответственный: ${users.data?.find((u) => u.id === id)?.fullName ?? 'никто'}`),
                onError: (e) => toast.showError(errorText(e)),
              })
            }
          />
          {main}
          {extra.length > 2 ? (
            <ActionMenu items={extra} />
          ) : (
            extra.map((i) => (
              <Button key={i.label} onClick={i.onClick}>
                {i.label}
              </Button>
            ))
          )}
        </>
      }
    />
  );
}

function LeadSignals({ lead: l }: { lead: LeadCard }) {
  const near = l.sameNumber.filter((m) => m.minutesApart < 60 && m.event?.id !== l.event?.id);
  const known = l.sameNumber.length || (l.client && !l.clientCreatedByLead);
  return (
    <Signals>
      {l.waiting && l.overThreshold && l.touchMinutes !== null ? (
        <Alert tone="crit">
          <b>Заявку никто не взял {minutesText(l.touchMinutes)}.</b>
          <span>Порог касания — {l.touchLimit} мин.</span>
        </Alert>
      ) : null}
      {known ? (
        <Alert tone="mut">
          <b>Номер уже знаком.</b>
          <span>
            {l.client && !l.clientCreatedByLead
              ? `Клиент в базе: ${l.client.fullName}${l.client.dealsCount ? ` · ${deals(l.client.dealsCount)}` : ''}. `
              : ''}
            {l.sameNumber.length ? `Других заявок с этого номера: ${l.sameNumber.length}` : ''}
            {near
              .map(
                (m) =>
                  ` · #${m.id} ${m.minutesApart} мин ${m.createdAt < l.createdAt ? 'раньше' : 'позже'} — ${m.event ? m.event.city : 'без мероприятия'}`,
              )
              .join('')}
            {l.sameNumber.length ? '. ' : ''}
            Квалификация привяжет заявку к существующей сделке, если она есть на то же мероприятие.
          </span>
        </Alert>
      ) : null}
    </Signals>
  );
}

const none = <span className="dim">не передано</span>;

function LeadPanel({ lead: l }: { lead: LeadCard }) {
  const taken = l.takenAt ? dateTime(l.takenAt) : null;
  return (
    <Panel title="Заявка">
      <Kv k="Телефон">
        <span className="num">{formatPhone(l.phone)}</span>
      </Kv>
      <Kv k="Имя / ФИО">{l.contactName ?? none}</Kv>
      <Kv k="Почта">{l.email ?? none}</Kv>
      <Kv k="Мероприятие">{l.event?.label ?? <span className="dim">не указано</span>}</Kv>
      <Kv k="Комментарий с формы">
        {l.message?.trim() ? <span className="mline">{l.message}</span> : <span className="dim">не передан</span>}
      </Kv>
      <Kv k="Поступила">
        <span className="num">{fullDateTime(l.createdAt)}</span>
      </Kv>
      <Kv k="Касание">
        {l.touchMinutes === null ? (
          <span className="na">—</span>
        ) : taken ? (
          <>
            <span className={l.overThreshold ? 'ctx' : ''}>{minutesText(l.touchMinutes)}</span>{' '}
            <span className="muted">
              · взята {taken.date} в {taken.time}
            </span>
          </>
        ) : (
          <span className={l.overThreshold ? 'ctx' : 'wtx'}>не взята · ждёт {minutesText(l.touchMinutes)}</span>
        )}
      </Kv>
      {l.status === 'rejected' && l.rejectReason ? (
        <Kv k="Причина отказа">
          {l.rejectReason.name}
          {l.rejectComment ? <div className="sub">{l.rejectComment}</div> : null}
        </Kv>
      ) : null}
      {l.status === 'duplicate' && l.duplicateOf ? <DuplicateLink id={l.duplicateOf} /> : null}
    </Panel>
  );
}

function DuplicateLink({ id }: { id: number }) {
  const navigate = useNavigate();
  return (
    <Kv k="Дубль заявки">
      <span className="lnk" onClick={() => navigate(`/crm/leads/${id}`)}>
        #{id}
      </span>
    </Kv>
  );
}

function ClientPanel({ lead: l }: { lead: LeadCard }) {
  const navigate = useNavigate();
  const c = l.client;
  return (
    <Panel title="Клиент">
      {c ? (
        <div className="lrow" role="link" onClick={() => navigate(`/clients/${c.id}`)}>
          <Avatar initials={c.named ? c.initials : '#'} size={32} />
          <div className="x">
            <div className="n">{c.fullName}</div>
            <div className="s">
              {c.createdByLead && !c.lastName
                ? 'заведён заявкой · вместо имени телефон, ФИО — при квалификации'
                : [c.phone ? formatPhone(c.phone) : null, c.city?.name, c.studio ? `студия «${c.studio}»` : null]
                    .filter(Boolean)
                    .join(' · ')}
            </div>
          </div>
          <div className="v" />
          <span className="chev">›</span>
        </div>
      ) : (
        <PanelEmpty>Клиент не заведён: заявка пришла до перехода на «клиента сразу».</PanelEmpty>
      )}
    </Panel>
  );
}

function SourcePanel({ lead: l }: { lead: LeadCard }) {
  const manual = l.createdBy === 'manager';
  return (
    <Panel title="Источник">
      <Kv k="Канал">
        {l.source.name}
        {l.isMini ? <span className="muted"> · мини-заявка менеджера</span> : null}
      </Kv>
      <Kv k="Кто завёл">{manual ? 'менеджер вручную' : 'система, приём с канала'}</Kv>
      <Kv k="utm_source">{l.utm.source ?? none}</Kv>
      <Kv k="utm_medium">{l.utm.medium ?? none}</Kv>
      <Kv k="utm_campaign">{l.utm.campaign ?? none}</Kv>
      <Kv k="utm_content">{l.utm.content ?? none}</Kv>
      <Kv k="utm_term">{l.utm.term ?? none}</Kv>
      <Kv k="Страница входа">{l.landingPage ? <span className="brk">{l.landingPage}</span> : none}</Kv>
      <Kv k="Реферер">{l.referrer ? <span className="brk">{l.referrer}</span> : none}</Kv>
      {l.utm.campaign || l.landingPage || l.hasRawPayload ? (
        <Note>
          Метки хранятся в заявке и не копируются в сделку: источник сделки и номера читается по цепочке. Сырые данные
          формы сохранены целиком.
        </Note>
      ) : null}
    </Panel>
  );
}
